import React, {
  FunctionComponent,
  useCallback,
  KeyboardEvent,
  useRef,
  MouseEventHandler,
  ReactNode,
} from 'react'
import {
  Direction,
  QuickKey,
  QuickKeyBlank,
  QuickKeyFolder,
  QuickKeyKey,
  QuickKeyRoot,
} from './utils/qk-types'
import { navigate } from './utils/qk-navigation'
import { QuickKeyNodeType } from './utils/qk.constants'

const KEY_MAP: Record<string, Direction> = {
  ArrowLeft: 'LEFT',
  ArrowRight: 'RIGHT',
  ArrowUp: 'UP',
  ArrowDown: 'DOWN',
}

interface QuickKeysKeyboardNavProps {
  children: ReactNode
  className?: string
  layout: QuickKeyRoot
  currentRoot: QuickKeyFolder | null
  focussed: QuickKeyFolder | QuickKeyKey | QuickKeyBlank | null
  includeBlanks?: boolean
  setFocussed: (
    node: QuickKeyFolder | QuickKeyKey | QuickKeyBlank | null
  ) => void
  setCurrentRoot: (node: QuickKeyFolder | null) => void
  selectQuickKey: (quickKey: QuickKey | null) => void
}

const getFirstChild = (node: QuickKey) => {
  if (!('children' in node && node.children.length)) {
    return null
  }

  const firstChild = node.children[0]
  if (
    firstChild.type === QuickKeyNodeType.FOLDER ||
    firstChild.type === QuickKeyNodeType.QUICK_KEY
  ) {
    return firstChild
  }

  return getFirstChild(firstChild)
}

export const QuickKeysKeyboardNav: FunctionComponent<QuickKeysKeyboardNavProps> = ({
  children,
  className,
  layout,
  currentRoot,
  focussed,
  includeBlanks,
  setFocussed,
  setCurrentRoot,
  selectQuickKey,
}) => {
  const container = useRef<HTMLDivElement>(null)

  const onFocus = useCallback(() => {
    const root = currentRoot || layout
    if (!focussed && root) {
      const firstChild = getFirstChild(root)
      setFocussed(firstChild || null)
    }
  }, [currentRoot, layout, focussed, setFocussed])

  const handleGetFocus: MouseEventHandler<HTMLDivElement> = useCallback(
    e => {
      if (
        container.current &&
        !container.current.contains(document.activeElement)
      ) {
        const currentFocus = focussed
        setFocussed(null)
        setTimeout(() => {
          setFocussed(currentFocus)
        })
      }
    },
    [focussed, setFocussed]
  )

  const keyDownHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const handleDirectionKey = (direction: Direction) => {
        if (!layout) {
          return
        }
        e.preventDefault()
        const pageIndex =
          focussed && 'parentNode' in focussed
            ? focussed.parentNode.position
            : 0
        const fromPosition = {
          position: focussed?.node.position || 0,
          pageIndex,
        }
        const result = navigate(
          currentRoot || layout,
          {
            fromPosition,
            direction,
          },
          { includeBlanks }
        )
        if (
          result.to?.type === QuickKeyNodeType.FOLDER ||
          result.to?.type === QuickKeyNodeType.QUICK_KEY ||
          (result.to?.type === QuickKeyNodeType.BLANK && includeBlanks)
        ) {
          setFocussed(result.to)
        } else {
          setFocussed(null)
        }
      }

      switch (e.key) {
        case 'Enter':
          e.preventDefault()
          selectQuickKey(focussed)
          break
        case 'Escape':
          if (!currentRoot) {
            return
          }
          e.preventDefault()
          setFocussed(currentRoot)
          setCurrentRoot(null)
          break
        case 'ArrowLeft':
          handleDirectionKey(KEY_MAP.ArrowLeft)
          break
        case 'ArrowRight':
          handleDirectionKey(KEY_MAP.ArrowRight)
          break
        case 'ArrowUp':
          handleDirectionKey(KEY_MAP.ArrowUp)
          break
        case 'ArrowDown':
          handleDirectionKey(KEY_MAP.ArrowDown)
          break
      }
    },
    [
      currentRoot,
      focussed,
      includeBlanks,
      layout,
      selectQuickKey,
      setCurrentRoot,
      setFocussed,
    ]
  )
  return (
    <div
      data-testid="quick-keys-container"
      className={className}
      onFocus={onFocus}
      onKeyDown={keyDownHandler}
      ref={container}
      onClick={handleGetFocus}
      tabIndex={focussed ? undefined : 0}
    >
      {children}
    </div>
  )
}
